import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../utils/analytics';
import { captureUtmFromUrl } from '../utils/utm';

/**
 * Page title configuration for different routes
 */
const PAGE_TITLES = {
  '/': 'SnagEasy - Professional Home Inspection Services | Defect Identification & Verification',
  '/home-inspection': 'Home Inspection Services | SnagEasy - Professional Property Inspection',
  '/home-inspection-faq': 'Home Inspection FAQ | Frequently Asked Questions | SnagEasy',
  '/legal-property-services': 'Legal Property Verification Services | SnagEasy',
  '/blog': 'Blog | Home Inspection Tips & Property Insights | SnagEasy',
  '/become-a-partner': 'Become a Partner | Join SnagEasy Network | Partnership Opportunities',
  '/book-inspection': 'Book Your Inspection | Schedule Property Inspection | SnagEasy',
};

/**
 * Get page title for a given path
 */
const getPageTitle = (pathname) => {
  // Check exact matches first
  if (PAGE_TITLES[pathname]) {
    return PAGE_TITLES[pathname];
  }
  
  // Check dynamic routes
  // Note: Blog post titles are set by BlogPage component itself
  // This is a fallback in case the component hasn't set it yet
  if (pathname.startsWith('/blog/')) {
    return 'Blog Post | SnagEasy';
  }
  
  if (pathname.startsWith('/service/')) {
    const serviceName = pathname.split('/service/')[1];
    const formattedName = serviceName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return `${formattedName} | SnagEasy - Professional Home Inspection Services`;
  }
  
  // Default title
  return 'SnagEasy - Professional Home Inspection Services';
};

/**
 * Custom hook to track page views in React Router
 * Automatically tracks page views when route changes
 * Also updates document.title for proper SEO and GA tracking
 * 
 * Note: Blog posts are tracked by BlogPage component itself to use dynamic titles
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    captureUtmFromUrl();

    // Skip tracking for blog posts - BlogPage component handles its own tracking
    if (location.pathname.startsWith('/blog/')) {
      // Still update title as fallback, but don't track (BlogPage will track with correct title)
      const pageTitle = getPageTitle(location.pathname);
      document.title = pageTitle;
      return;
    }
    
    // Update page title based on route
    const pageTitle = getPageTitle(location.pathname);
    document.title = pageTitle;
    
    // Track page view with updated title
    trackPageView(location.pathname + location.search, pageTitle);
  }, [location]);
};

export default usePageTracking;

